<template>
  <div class="md-layout">
    <!-- Status Cards -->
    <div class="md-layout-item md-medium-size-33 md-xsmall-size-50 md-size-20"
         v-for="status in statuses"
         :key="status.key"
         @click="workplaceStatuesFilter(status.key)">
      <md-card :class="{ 'active-status-card': filteredStatus === status.key }">
        <md-card-header :class="'md-card-header-icon md-card-header-' + status.color">
          <div class="card-icon">
            <md-icon>{{ status.icon }}</md-icon>
          </div>
          <h4 class="title">{{ status.title }}</h4>
        </md-card-header>
        <md-card-content>
          <div class="md-layout text-center text-primary">
            <h3>{{ status.count }}</h3>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>tag</md-icon>
          </div>
          <h4 class="title">Workplaces List</h4>
        </md-card-header>

        <md-card-content>
          <div class="text-right">
            <md-button @click="goToAdd" class="md-primary md-dense">
              <md-icon>add</md-icon>
              Add Workplace
            </md-button>
          </div>

          <!-- Toolbar -->
          <div class="table-toolbar">
            <div class="per-page-selector">
              <span>Show:</span>
              <md-select v-model="pagination.perPage" @md-selected="onPerPageChange" class="per-page-select">
                <md-option v-for="item in pagination.perPageOptions" :key="item" :value="item">
                  {{ item }}
                </md-option>
              </md-select>
              <span>entries</span>
            </div>

            <md-field class="search-field">
              <md-icon>search</md-icon>
              <md-input
                  v-model="searchQuery"
                  type="search"
                  placeholder="Search by name, location, item number..."
                  @input="onSearchChange"
              />
            </md-field>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="loading-container">
            <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
          </div>

          <!-- Table -->
          <div v-else class="table-container">
            <md-table v-if="table && table.length">
              <md-table-row>
                <md-table-head @click="sortBy('name')" :class="{ 'sort-active': sortation.field === 'name' }">
                  Name
                  <md-icon class="sort-icon" v-if="sortation.field === 'name'">
                    {{ sortation.order === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                  </md-icon>
                </md-table-head>
                <md-table-head @click="sortBy('item_number')"
                               :class="{ 'sort-active': sortation.field === 'item_number' }">
                  Item Number
                  <md-icon class="sort-icon" v-if="sortation.field === 'item_number'">
                    {{ sortation.order === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                  </md-icon>
                </md-table-head>
                <md-table-head @click="sortBy('location')" :class="{ 'sort-active': sortation.field === 'location' }">
                  Location
                  <md-icon class="sort-icon" v-if="sortation.field === 'location'">
                    {{ sortation.order === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                  </md-icon>
                </md-table-head>
                <md-table-head @click="sortBy('status')" :class="{ 'sort-active': sortation.field === 'status' }">
                  Status
                </md-table-head>
                <md-table-head>Activities</md-table-head>
                <md-table-head>Labourers</md-table-head>
                <md-table-head>Guidance</md-table-head>
                <md-table-head @click="sortBy('created_at')"
                               :class="{ 'sort-active': sortation.field === 'created_at' }">Created At
                </md-table-head>
                <md-table-head>Actions</md-table-head>
              </md-table-row>

              <md-table-row v-for="item in table" :key="item.id">
                <md-table-cell>
                  {{ item.name }}<br/>
                  <Badge type="info">{{getPortName(item)}}</Badge>
                </md-table-cell>
                <md-table-cell>{{ item.item_number || '-' }}</md-table-cell>
                <md-table-cell>{{ item.location || '-' }}</md-table-cell>
                <md-table-cell>
                  <badge :type="getStatusBadge(item.status)">{{ item.status || 'Active' }}</badge>
                </md-table-cell>
                <md-table-cell>
                  <span class="clickable-count" @click="showActivities(item)">
                    {{ formatNumber(item.activities_count) }}
                  </span>
                </md-table-cell>
                <md-table-cell>
                  <span class="clickable-count" @click="showLabourers(item)">
                    {{ formatNumber(item.activity_labourer_count) }}
                  </span>
                </md-table-cell>
                <md-table-cell>
                  <span class="clickable-count" @click="showGuidances(item)">
                    {{ getActiveGuidanceCount(item) }}
                  </span>
                </md-table-cell>
                <md-table-cell>{{ formatDate(item.created_at) }}</md-table-cell>
                <md-table-cell>
                  <md-button class="md-icon-button md-primary md-sm" @click="goToView(item.id)" title="View">
                    <md-icon>visibility</md-icon>
                  </md-button>
                  <md-button class="md-icon-button md-info md-sm" @click="goToEdit(item.id)" title="Edit">
                    <md-icon>edit</md-icon>
                  </md-button>
                  <md-button class="md-icon-button md-danger md-sm" @click="destroy(item.id)" title="Delete">
                    <md-icon>delete</md-icon>
                  </md-button>
                </md-table-cell>
              </md-table-row>
            </md-table>

            <div v-else class="empty-state">
              <md-icon>business</md-icon>
              <p>No workplaces found</p>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-footer" v-if="total > 0">
            <div class="pagination-info">
              Showing {{ from + 1 }} to {{ to }} of {{ formatNumber(total) }} entries
            </div>
            <pagination
                class="pagination-no-border pagination-success"
                v-model="pagination.currentPage"
                :per-page="pagination.perPage"
                :total="total"
            />
          </div>
        </md-card-content>
      </md-card>
    </div>

    <!-- Dialog remains the same -->
    <md-dialog :md-active.sync="dialog.active">
      <md-dialog-title>{{ dialog.title }}</md-dialog-title>
      <md-dialog-content>
        <div class="dialog-content">
          <div v-if="dialog.items && dialog.items.length">
            <md-list>
              <md-list-item v-for="(item, index) in dialog.items" :key="index">
                <md-icon>{{ getItemIcon(dialog.type) }}</md-icon>
                <span class="dialog-item-text">{{ item.name || item.title || item }}</span>
              </md-list-item>
            </md-list>
          </div>
          <div v-else class="empty-dialog">
            <md-icon>info</md-icon>
            <p>No data available</p>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="dialog.active = false">Close</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import _ from 'lodash';
import Pagination from '@/components/Pagination';
import Badge from '@/components/Badge.vue';
import Swal from 'sweetalert2';
import formMixin from '@/mixins/form-mixin';

export default {
  name: 'WorkplacesListPage',
  components: {
    pagination: Pagination,
    Badge
  },
  mixins: [formMixin],
  data: () => ({
    table: [],
    statuses: [],
    searchQuery: '',
    sortation: {
      field: 'created_at',
      order: 'desc',
    },
    pagination: {
      perPage: 20,
      currentPage: 1,
      perPageOptions: [10, 20, 50, 100, 200, 500],
    },
    filteredStatus: null,
    total: 0,
    isLoading: false,
    hasRestoredState: false,
    dialog: {
      active: false,
      title: '',
      items: [],
      type: ''
    }
  }),

  computed: {
    sort() {
      if (this.sortation.order === 'desc') {
        return `-${this.sortation.field}`;
      }
      return this.sortation.field;
    },

    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    // Watch for pagination currentPage changes
    'pagination.currentPage': {
      handler: function(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log('Page changed from', oldVal, 'to', newVal);
          this.getList();
        }
      }
    },

    // Watch for perPage changes
    'pagination.perPage': {
      handler: function(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.pagination.currentPage = 1;
          this.getList();
        }
      }
    },

    // Watch for route changes to restore state when coming back
    '$route': {
      handler: function(to, from) {
        if (to.name === 'Workplaces List' && from && from.name !== 'Workplaces List') {
          this.restoreStateFromSession();
        }
      }
    },

    // Save state when values change
    sortation: {
      handler: function() {
        this.saveStateToSession();
      },
      deep: true
    },

    searchQuery: {
      handler: function() {
        this.saveStateToSession();
      }
    },

    filteredStatus: {
      handler: function() {
        this.saveStateToSession();
      }
    }
  },

  created() {
    this.restoreStateFromSession();
    this.getStatusCounts();

    if (!this.hasRestoredState) {
      this.getList();
    }
  },

  beforeRouteLeave(to, from, next) {
    this.saveStateToSession();
    next();
  },

  methods: {
    // ============ STATE PERSISTENCE ============
    saveStateToSession() {
      const state = {
        searchQuery: this.searchQuery,
        sortation: this.sortation,
        pagination: {
          perPage: this.pagination.perPage,
          currentPage: this.pagination.currentPage
        },
        filteredStatus: this.filteredStatus,
        timestamp: new Date().getTime()
      };
      sessionStorage.setItem('workplaces_list_state', JSON.stringify(state));
    },

    restoreStateFromSession() {
      const savedState = sessionStorage.getItem('workplaces_list_state');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          if (Date.now() - state.timestamp < 30 * 60 * 1000) {
            this.searchQuery = state.searchQuery || '';
            this.sortation = state.sortation || { field: 'created_at', order: 'desc' };
            this.pagination.perPage = state.pagination?state.pagination.perPage : 20;
            this.pagination.currentPage = state.pagination?state.pagination.currentPage : 1;
            this.filteredStatus = state.filteredStatus || null;
            this.hasRestoredState = true;
            this.getList();
            return true;
          } else {
            sessionStorage.removeItem('workplaces_list_state');
          }
        } catch (e) {
          console.error('Error restoring state:', e);
        }
      }
      this.hasRestoredState = false;
      return false;
    },

    // ============ DATA FETCHING ============
    async getStatusCounts() {
      try {
        await this.$store.dispatch('workplaces/statusStatistics');
        this.statuses = this.$store.getters['workplaces/statistics'] || [];
      } catch (e) {
        console.error('Failed to fetch status counts', e);
        this.statuses = [];
      }
    },

    async getList() {
      this.isLoading = true;

      try {
        let params = {
          sort: this.sort,
          filter: {},
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: 'port'
        };

        if (this.searchQuery && this.searchQuery.trim()) {
          params.filter.search = this.searchQuery.trim();
        }

        if (this.filteredStatus) {
          params.filter.status = this.filteredStatus;
        }

        console.log('Fetching page:', this.pagination.currentPage, 'Per page:', this.pagination.perPage);

        await this.$store.dispatch('workplaces/list', params);
        this.table = this.$store.getters['workplaces/list'] || [];
        this.total = this.$store.getters['workplaces/listTotal'] || 0;

        console.log('Total records:', this.total, 'Table length:', this.table.length);

      } catch (e) {
        console.error('Error fetching workplaces:', e);
        this.notifyError('Failed to load workplaces');
        this.table = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    // ============ FILTER HANDLERS ============
    workplaceStatuesFilter(status) {
      this.filteredStatus = this.filteredStatus === status ? null : status;
      this.pagination.currentPage = 1;
      this.getList();
    },

    onSearchChange: _.debounce(function() {
      this.pagination.currentPage = 1;
      this.getList();
    }, 500),

    onPerPageChange() {
      // This will trigger the watch on pagination.perPage
      // No need to call getList here, watch will handle it
    },

    sortBy(field) {
      if (this.sortation.field === field) {
        this.sortation.order = this.sortation.order === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortation.field = field;
        this.sortation.order = 'asc';
      }
      this.pagination.currentPage = 1;
      this.getList();
    },

    // ============ CRUD METHODS ============
    async destroy(id) {
      const result = await Swal.fire({
        title: 'Delete Workplace?',
        text: 'This action cannot be undone!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33'
      });

      if (result.value) {
        try {
          await this.$store.dispatch('workplaces/destroy', id);
          this.notifySuccess('Workplace deleted successfully');

          if (this.table.length === 1 && this.pagination.currentPage > 1) {
            this.pagination.currentPage--;
          }

          await this.getList();
          await this.getStatusCounts();
        } catch (e) {
          this.notifyException(e);
        }
      }
    },

    getPortName(item) {
      return item.port ? item.port.name : (item.port_id || '-');
    },

    getActiveGuidanceCount(item) {
      if (item.activeGuidance && Array.isArray(item.activeGuidance)) {
        return item.activeGuidance.length;
      }
      return 0;
    },

    getStatusBadge(status) {
      const badges = {
        'Active': 'success',
        'Inactive': 'danger',
        'Suspended': 'warning',
        'Operational': 'success',
        'Maintenance': 'warning'
      };
      return badges[status] || 'info';
    },

    getItemIcon(type) {
      const icons = {
        'activities': 'work',
        'labourers': 'people',
        'guidances': 'assignment'
      };
      return icons[type] || 'list';
    },

    async showActivities(workplace) {
      try {
        let activities = workplace.activities;
        if (!activities || !activities.length) {
          await this.$store.dispatch('workplaces/get', workplace.id);
          const fresh = this.$store.getters['workplaces/workplace'];
          activities = fresh ? fresh.activities : [];
        }
        this.dialog.title = `Activities (${activities.length}) - ${workplace.name}`;
        this.dialog.items = activities.map(a => ({ name: a.name }));
        this.dialog.type = 'activities';
        this.dialog.active = true;
      } catch (e) {
        this.notifyException(e);
      }
    },

    async showLabourers(workplace) {
      try {
        let labourers = [];
        if (workplace.activityLabourers) {
          labourers = workplace.activityLabourers.map(item => item.labourer).filter(l => l);
        }
        this.dialog.title = `Labourers (${labourers.length}) - ${workplace.name}`;
        this.dialog.items = labourers.map(l => ({
          name: `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.name || 'Unknown'
        }));
        this.dialog.type = 'labourers';
        this.dialog.active = true;
      } catch (e) {
        this.notifyException(e);
      }
    },

    async showGuidances(workplace) {
      try {
        let guidances = [];
        if (workplace.activeGuidance && Array.isArray(workplace.activeGuidance)) {
          guidances = workplace.activeGuidance;
        }
        this.dialog.title = `Active Guidances (${guidances.length}) - ${workplace.name}`;
        this.dialog.items = guidances.map(g => ({
          name: g.title || g.name || g.guidance_number || 'Unknown'
        }));
        this.dialog.type = 'guidances';
        this.dialog.active = true;
      } catch (e) {
        this.notifyException(e);
      }
    },

    formatNumber(value) {
      if (!value) return '0';
      return new Intl.NumberFormat().format(value);
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString();
    },

    // ============ NAVIGATION METHODS ============
    goToAdd() {
      this.saveStateToSession();
      this.$router.push({ name: 'Add Workplace' });
    },

    goToEdit(id) {
      this.saveStateToSession();
      this.$router.push({ name: 'Edit Workplace', params: { id } });
    },

    goToView(id) {
      this.saveStateToSession();
      this.$router.push({ name: 'View Workplace', params: { id } });
    },

    customSort() {
      return false;
    }
  }
};
</script>

<style scoped>
.active-status-card {
  border: 2px solid #2196f3;
  transform: scale(1.02);
  transition: all 0.3s ease;
}

.sort-active {
  cursor: pointer;
  user-select: none;
}

.sort-active:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;
  flex-wrap: wrap;
  gap: 12px;
}

.per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.per-page-select {
  width: 80px;
  margin: 0;
}

.search-field {
  margin: 0;
  width: 250px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.table-container {
  min-height: 400px;
  overflow-x: auto;
}

.sort-icon {
  font-size: 16px;
  margin-left: 4px;
  vertical-align: middle;
}

.clickable-count {
  cursor: pointer;
  color: #2196f3;
  font-weight: 500;
  transition: color 0.2s;
}

.clickable-count:hover {
  color: #0b5e8a;
  text-decoration: underline;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state md-icon {
  font-size: 64px;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
  flex-wrap: wrap;
  gap: 16px;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

.dialog-content {
  min-width: 400px;
  max-height: 500px;
  overflow-y: auto;
}

.dialog-item-text {
  margin-left: 12px;
}

.empty-dialog {
  text-align: center;
  padding: 40px;
  color: #999;
}

.md-button.md-sm {
  margin: 0 2px;
  width: 32px;
  min-width: 32px;
  height: 32px;
  padding: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .table-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-field {
    width: 100%;
  }

  .pagination-footer {
    flex-direction: column;
    align-items: center;
  }

  .dialog-content {
    min-width: auto;
  }
}
</style>